import {PIXI} from '@colonist/pixi'
import {UICanvasConfig} from './UICanvasConfig'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {UIPopupController} from '../ui_popup/UIPopupController'
import {CA} from '../analytics/Analytics'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../shared/entity_datas/EntityDatas'
import {TranslationKeys} from '@colonist/i18n'
import {StorageWrapper} from '../StorageWrapper'

export class UICanvasController {
    readonly CANVAS_ID: string = 'game-canvas'
    app: PIXI.Application<HTMLCanvasElement>
    private readonly uiCanvasConfig = UICanvasConfig

    createPIXIApplication(options: Partial<PIXI.IApplicationOptions>) {
        this.app = new PIXI.Application<HTMLCanvasElement>({
            autoDensity: true,
            ...options,
        })
        this.app.view.id = this.CANVAS_ID
        this.app.stage.sortableChildren = true
        PIXI.settings.RESOLUTION = options.resolution ?? 2
        this.registerPixiInspector()
        this.addCanvasWebGLContextLossEventListener()

        this.preventRightClick()
        this.preventMobileTextSelection()
    }

    registerPixiInspector() {
        if(UIUtilsEjsVariables.isProduction) return
        globalThis.__PIXI_APP__ = this.app
    }

    setCanvasConfigVariables() {
        UICanvasConfig.setCanvasConfigVariables(this.app)
    }

    get appW() {
        return this.uiCanvasConfig.appWidth
    }
    get appH() {
        return this.uiCanvasConfig.appHeight
    }
    get itemSize() {
        return this.uiCanvasConfig.itemSize
    }
    get margin() {
        return this.uiCanvasConfig.margin
    }

    updateCanvasSettings(options: Partial<PIXI.IApplicationOptions>) {
        const {resolution, antialias, powerPreference} = options

        if(resolution !== undefined) {
            this.app.renderer.resolution = resolution
            PIXI.settings.RESOLUTION = resolution
        }

        if(antialias !== undefined) {
            this.app.renderer.options.antialias = antialias
        }

        if(powerPreference !== undefined) {
            this.app.renderer.options.powerPreference = powerPreference
        }

        this.app.renderer.resize(this.appW, this.appH)
        this.app.render()
    }

    private addCanvasWebGLContextLossEventListener() {
        let contextLossReported = false
        this.app.view.addEventListener('webglcontextlost', async(event) => {
            event.preventDefault()
            if(!contextLossReported) {
                contextLossReported = true

                const memoryInfo = (window?.performance as any)?.memory

                const memoryData = {
                    jsHeapSizeLimit: memoryInfo?.jsHeapSizeLimit ?? 0,
                    totalJSHeapSize: memoryInfo?.totalJSHeapSize ?? 0,
                    usedJSHeapSize: memoryInfo?.usedJSHeapSize ?? 0,
                    isMobile: UIUtilsEjsVariables.isUserOnMobileAppOrWeb,
                    isDiscord: UIUtilsEjsVariables.isUserOnDiscord,
                    powerMode: StorageWrapper.getPowerPreference(),
                    antiAliasing: StorageWrapper.getAntialias(),
                    resolution: StorageWrapper.getResolution(),
                    userAgent: navigator.userAgent,
                }

                await CA.sendAnalyticEvent({
                    category: AnalyticEventCategory.WebGL,
                    type: AnalyticEventType.LostWebGLContext,
                    stringValue: JSON.stringify(memoryData),
                })
            }

            const popupBody: TranslationKeys = UIUtilsEjsVariables.isUserOnDiscord ? 'strings:popups.webglContextLoss.discord' : 'strings:popups.webglContextLoss.body'

            UIPopupController.createSimplePopup({key: 'strings:popups.webglContextLoss.title'}, {key: popupBody})
        })
    }

    private preventRightClick() {
        this.app.view.addEventListener('contextmenu', (e) => e.preventDefault())
    }

    private preventMobileTextSelection() {
        this.app.view.addEventListener('selectstart', (e) => e.preventDefault())
    }
}
